'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';

export type FeatureHeroProps = {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  features: {
    title: string;
    description: string;
  }[];
};

export function FeatureHero({
  icon,
  title,
  subtitle,
  description,
  imageSrc,
  imageAlt,
  features
}: FeatureHeroProps): React.JSX.Element {
  return (
    <GridSection>
      <div className="py-20">
        {/* Hero Section */}
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10">
              {icon}
            </div>
            <p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary">
              {subtitle}
            </p>
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {title}
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground md:text-xl">
              {description}
            </p>
            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                href={routes.dashboard.auth.SignUp}
                className={cn(buttonVariants({ size: 'lg' }), 'rounded-xl')}
              >
                Get Started
              </Link>
              <Link
                href={routes.marketing.Contact}
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'lg' }),
                  'rounded-xl'
                )}
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Screenshot */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-16 px-4 sm:px-8 md:px-12 lg:px-16"
        >
          <div className="mx-auto max-w-6xl">
            <Image
              src={imageSrc}
              alt={imageAlt}
              width={1400}
              height={850}
              quality={100}
              className="w-full rounded-xl border shadow-2xl"
              priority
            />
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="container mt-24">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="rounded-xl border bg-card p-6"
              >
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </GridSection>
  );
}
